import React, { useState, useEffect } from "react";
import { notificationService, UserNotification } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { Bell, Mail, MailOpen, Info, CheckCircle, Clock, User, FileX, Ban, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { enGB } from "date-fns/locale";
import toast from "react-hot-toast";

const Notifications: React.FC = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (user) {
      loadNotifications();
    }
  }, [user, filter]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationService.getNotifications(0, 50, filter === "unread");

      setNotifications(response.notifications);
      setTotal(response.total);
      setUnreadCount(response.unread_count);
    } catch (error) {
      toast.error("Error loading notifications");
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId);

      // Update local state
      setNotifications((prev) => prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n)));

      // Update unread count
      setUnreadCount((prev) => Math.max(0, prev - 1));

      toast.success("Notification marked as read");
    } catch (error) {
      toast.error("Error marking notification as read");
    }
  };

  const handleDelete = async (notificationId: string) => {
    try {
      await notificationService.deleteNotification(notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      toast.success("Notification deleted");
    } catch (error) {
      toast.error("Error deleting notification");
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "post_deleted":
        return <FileX className="w-5 h-5 text-red-500" />;
      case "ban_notice":
        return <Ban className="w-5 h-5 text-red-500" />;
      case "general":
        return <Info className="w-5 h-5 text-blue-500" />;
      default:
        return <Bell className="w-5 h-5 text-newspaper-500" />;
    }
  };

  const getNotificationStyle = (type: string, read: boolean) => {
    const baseStyle = "border-l-4 ";
    const readStyle = read ? "bg-newspaper-50" : "bg-white shadow-sm";

    switch (type) {
      case "post_deleted":
      case "ban_notice":
        return baseStyle + "border-red-500 " + readStyle;
      case "general":
        return baseStyle + "border-blue-500 " + readStyle;
      default:
        return baseStyle + "border-newspaper-300 " + readStyle;
    }
  };

  if (!user) {
    return (
      <div className="beat-container">
        <div className="beat-card p-8 text-center">
          <Bell className="w-16 h-16 mx-auto mb-4 text-newspaper-400" />
          <h2 className="text-2xl font-bold text-newspaper-900 mb-2">Sign In</h2>
          <p className="text-newspaper-600">You must sign in to view your notifications.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="beat-container">
      {/* Header */}
      <div className="beat-card p-8 mb-8 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Bell className="w-8 h-8 text-newspaper-900 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-newspaper-900">Notifications</h1>
              <p className="text-newspaper-600 mt-1">
                {unreadCount > 0
                  ? `You have ${unreadCount} unread notification${unreadCount !== 1 ? "s" : ""}`
                  : "You are up to date with all notifications"}
              </p>
            </div>
          </div>

          {/* Filter Buttons */}
          <div className="flex space-x-2">
            <button
              onClick={() => setFilter("all")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colours ${
                filter === "all"
                  ? "bg-newspaper-900 text-white"
                  : "bg-newspaper-100 text-newspaper-700 hover:bg-newspaper-200"
              }`}
            >
              All ({total})
            </button>
            <button
              onClick={() => setFilter("unread")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === "unread"
                  ? "bg-newspaper-900 text-white"
                  : "bg-newspaper-100 text-newspaper-700 hover:bg-newspaper-200"
              }`}
            >
              Unread ({unreadCount})
            </button>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-4">
        {loading ? (
          // Loading state
          [...Array(5)].map((_, i) => (
            <div key={i} className="beat-card p-6 rounded-lg">
              <div className="flex items-start space-x-4">
                <div className="beat-loading w-10 h-10 rounded-full"></div>
                <div className="flex-1">
                  <div className="beat-loading h-5 mb-2"></div>
                  <div className="beat-loading h-4 mb-2"></div>
                  <div className="beat-loading h-16"></div>
                </div>
              </div>
            </div>
          ))
        ) : notifications.length === 0 ? (
          // Empty state
          <div className="beat-card p-12 text-center rounded-lg">
            <Bell className="w-16 h-16 mx-auto mb-4 text-newspaper-400" />
            <h3 className="text-xl font-semibold text-newspaper-700 mb-2">
              {filter === "unread" ? "You have no unread notifications" : "You have no notifications"}
            </h3>
            <p className="text-newspaper-600">
              {filter === "unread"
                ? "All your notifications have been read."
                : "Notifications will appear here when you have important updates."}
            </p>
          </div>
        ) : (
          // Notifications
          notifications.map((notification) => (
            <div
              key={notification.id}
              className={`beat-card rounded-lg p-6 ${getNotificationStyle(
                notification.type,
                notification.read || false
              )}`}
            >
              <div className="flex items-start space-x-4">
                {/* Notification Icon */}
                <div className="flex-shrink-0 mt-1">{getNotificationIcon(notification.type)}</div>

                {/* Notification Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-newspaper-900">{notification.title}</h4>

                    <div>
                      {!notification.read && (
                        <button
                          onClick={() => markAsRead(notification.id)}
                          className="ml-4 p-1 text-newspaper-500 hover:text-newspaper-700 transition-colors"
                          title="Mark as read"
                        >
                          <MailOpen className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(notification.id)}
                        className="ml-4 p-1 text-red-500 hover:text-red-700 transition-colors"
                        title="Delete notification"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <p className="text-newspaper-700 mb-3 whitespace-pre-wrap">{notification.message}</p>

                  <div className="flex items-center justify-between text-sm text-newspaper-600">
                    <div className="flex items-center space-x-4">
                      <span className="flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {formatDistanceToNow(new Date(notification.created_at), {
                          addSuffix: true,
                          locale: enGB,
                        })}
                      </span>

                      {notification.created_by && (
                        <span className="flex items-center">
                          <User className="w-3 h-3 mr-1" />
                          By: {notification.created_by}
                        </span>
                      )}

                      {notification.post_title && (
                        <span className="flex items-center">
                          <FileX className="w-3 h-3 mr-1" />
                          Post: {notification.post_title}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center space-x-2">
                      {!notification.read ? (
                        <span className="flex items-center text-xs font-medium text-blue-600">
                          <Mail className="w-3 h-3 mr-1" />
                          New
                        </span>
                      ) : (
                        <span className="flex items-center text-xs text-newspaper-500">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Read
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}

        {/* Load More */}
        {notifications.length > 0 && notifications.length < total && (
          <div className="text-center py-6">
            <button
              onClick={() => {
                // Implement load more functionality
                toast.loading("Load more functionality coming soon");
              }}
              className="beat-button-secondary"
            >
              Load more notifications
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
